import {
  getReportByPage,
  type ReportRequestOptions,
} from '@services/api/reports/byPage.api'
import type {
  ReportBucketType,
  ReportKpiScalarResult,
  ReportPeriodSeriesResult,
  ReportQueryParams,
} from '@services/api/reports/finance.api'

export type PatientPopulationTrendBucket = {
  label: string
  active: number
  dormant: number
  lost: number
}

export type PatientPopulationTrendResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: PatientPopulationTrendBucket[]
}

export type AtRiskAgeBucketResult = {
  label: string
  value: number
  percentage: number
}

export type AtRiskByAgeResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: AtRiskAgeBucketResult[]
}

export type AtRiskPatientApiRow = {
  patientId: string
  patientName: string
  lastVisitDaysAgo: number
  unscheduledTreatment: number
  noShowCount: number
  outstandingBalance: number
  riskScore: number
  riskLevel: 'High' | 'Medium' | 'Low'
}

export type AtRiskPatientsResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  summary: {
    high: number
    medium: number
    low: number
    revenueAtRisk: number
  }
  patients: AtRiskPatientApiRow[]
}

export type PatientBaseHealthReportResult = {
  totalActivePatients: ReportKpiScalarResult
  totalActivePatientsFooter: ReportKpiScalarResult
  netActiveGrowth: ReportKpiScalarResult
  netActiveGrowthFooter: ReportKpiScalarResult
  retentionRate: ReportKpiScalarResult
  retentionRateFooter: ReportKpiScalarResult
  reactivationRate: ReportKpiScalarResult
  reactivationRateFooter: ReportKpiScalarResult
  atRiskPatientCount: ReportKpiScalarResult
  atRiskPatientCountFooter: ReportKpiScalarResult
  patientPopulationTrend: PatientPopulationTrendResult
  lastVisitDistribution: ReportPeriodSeriesResult
  atRiskByAge: AtRiskByAgeResult
  atRiskPatients: AtRiskPatientsResult
}

export const getPatientBaseHealthReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<PatientBaseHealthReportResult> =>
  getReportByPage<PatientBaseHealthReportResult>(
    '/patients/patient-base-health',
    params,
    options,
  )

export type ProcedureMixCategoryResult = {
  id: string
  label: string
  amount: number
  percentage: number
}

export type ProductionByProcedureMixResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  total: number
  categories: ProcedureMixCategoryResult[]
}

export type ActivePatientValueSegmentResult = {
  id: string
  label: string
  count: number
  percentage: number
}

export type ActivePatientValueSegmentsResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  segments: ActivePatientValueSegmentResult[]
  highValueRevenueShare: number
}

export type NewVsExistingProductionRow = {
  label: 'New Patient' | 'Existing Patient'
  patients: number
  revenue: number
}

export type NewVsExistingProductionResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: NewVsExistingProductionRow[]
}

export type ArUnscheduledByProviderApiRow = {
  providerId: string
  providerName: string
  patients: number
  unscheduledValue: number
  outstandingAr: number
}

export type ArUnscheduledByProviderResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: ArUnscheduledByProviderApiRow[]
}

export type PatientValueRevenueReportResult = {
  averagePatientValue: ReportKpiScalarResult
  averagePatientValueFooter: ReportKpiScalarResult
  activePatientRevenue: ReportKpiScalarResult
  activePatientRevenueFooter: ReportKpiScalarResult
  unscheduledTreatment: ReportKpiScalarResult
  unscheduledTreatmentFooter: ReportKpiScalarResult
  totalOutstandingAr: ReportKpiScalarResult
  totalOutstandingArFooter: ReportKpiScalarResult
  productionByProcedureMix: ProductionByProcedureMixResult
  activePatientValueSegments: ActivePatientValueSegmentsResult
  newVsExistingProduction: NewVsExistingProductionResult
  arUnscheduledByProvider: ArUnscheduledByProviderResult
}

export const getPatientValueRevenueReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<PatientValueRevenueReportResult> =>
  getReportByPage<PatientValueRevenueReportResult>(
    '/patients/patient-value-rev-distribution',
    params,
    options,
  )

export type UnscheduledProductionBucket = {
  label: string
  patients: number
  revenue: number
}

export type UnscheduledByProductionBucketResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: UnscheduledProductionBucket[]
}

export type OverdueVisitBucket = {
  label: string
  count: number
  percentage: number
}

export type OverdueVisitGroupingResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: OverdueVisitBucket[]
}

export type MedicalAlertCategoryApiRow = {
  id: string
  category: string
  patients: number
}

export type MedicalAlertCategoriesResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: MedicalAlertCategoryApiRow[]
}

export type PatientRiskComplianceReportResult = {
  patientsWithUnscheduledTreatments: ReportKpiScalarResult
  patientsWithUnscheduledTreatmentsFooter: ReportKpiScalarResult
  patientsWithMedicalAlerts: ReportKpiScalarResult
  patientsWithMedicalAlertsFooter: ReportKpiScalarResult
  patientsOverdue: ReportKpiScalarResult
  patientsOverdueFooter: ReportKpiScalarResult
  unscheduledByProductionBucket: UnscheduledByProductionBucketResult
  overdueVisitGrouping: OverdueVisitGroupingResult
  medicalAlertCategories: MedicalAlertCategoriesResult
}

export const getPatientRiskComplianceReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<PatientRiskComplianceReportResult> =>
  getReportByPage<PatientRiskComplianceReportResult>(
    '/patients/patient-risk-compliance',
    params,
    options,
  )

export type SameFamilyCategoryBucket = {
  label: string
  count: number
  percentage: number
}

export type SameFamilyByCategoryResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: SameFamilyCategoryBucket[]
}

export type MultiFamilyGapBucket = {
  label: string
  count: number
  percentage: number
}

export type MultiFamilyGapBySpreadResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: MultiFamilyGapBucket[]
}

export type BirthdayMilestoneApiRow = {
  id: string
  patientName: string
  date: string
  household: string
}

export type BirthdayMilestonesResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: BirthdayMilestoneApiRow[]
}

export type HouseholdSchedulingGapApiRow = {
  id: string
  household: string
  members: string
  dueUnscheduled: string
}

export type HouseholdSchedulingGapsResult = {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: HouseholdSchedulingGapApiRow[]
}

export type FamilyOpportunityIntelligenceReportResult = {
  sameFamilyOpportunities: ReportKpiScalarResult
  sameFamilyOpportunitiesFooter: ReportKpiScalarResult
  householdsWithGaps: ReportKpiScalarResult
  householdsWithGapsFooter: ReportKpiScalarResult
  upcomingBirthdays: ReportKpiScalarResult
  upcomingBirthdaysFooter: ReportKpiScalarResult
  multiFamilySchedulingGaps: ReportKpiScalarResult
  multiFamilySchedulingGapsFooter: ReportKpiScalarResult
  sameFamilyByCategory: SameFamilyByCategoryResult
  multiFamilyGapBySpread: MultiFamilyGapBySpreadResult
  birthdayMilestones: BirthdayMilestonesResult
  householdSchedulingGaps: HouseholdSchedulingGapsResult
}

export const getFamilyOpportunityIntelligenceReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<FamilyOpportunityIntelligenceReportResult> =>
  getReportByPage<FamilyOpportunityIntelligenceReportResult>(
    '/patients/family-opportunity-intelligence',
    params,
    options,
  )
